/**
 * Minimal thrown-value-to-text coercion. Pure-leaf module: NO value imports.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): generic coercer homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. This body was spelled inline (or as a local
 * `errMsg()` helper) at fourteen utils call sites because the canonical `getErrorMessage`
 * (utils/errors/error-core.ts) is a utils sibling those leaves may not value-import.
 *
 * Boundary vs `getErrorMessage`, load-bearing and NOT interchangeable: `getErrorMessage` additionally reads a
 * string `.message` off a plain thrown OBJECT and falls back to `ERROR_MESSAGES.UNKNOWN_ERROR`; this helper
 * coerces every non-Error via `String()` (a message-carrying object renders as `[object Object]`). Callers that
 * want the object-message/fallback semantics use `getErrorMessage`; callers that want raw coercion use this.
 * That object-message arm now lives in this leaf too, as {@link errorMessageOrNull} -- the shared body BEHIND
 * `getErrorMessage`, kept separate from this one because the two answer a non-Error differently on purpose.
 */
export function errorMessageOrString(error: unknown): string {
	return error instanceof Error ? error.message : String(error);
}

/** The row a walk keeps for a path it could not read: the path and the throw's text -- the standard sheet's unloadable
 *  list and the record corpus loader's unreadable list each pushed `{ path, reason: errorMessageOrString(err) }`
 *  (2026-09-28 fold).
 *  @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-file {@link errorMessageOrString}. */
export function unreadablePathRow(path: string, error: unknown): { readonly path: string; readonly reason: string } {
	return { path, reason: errorMessageOrString(error) };
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the OBJECT-MESSAGE-READING extractor, homed beside
 * {@link errorMessageOrString} for the same reason it is -- a pure utils/ leaf may not value-import the utils sibling
 * where the canonical reader lives. THREE arms and no fallback: a real `Error`'s `.message`, a thrown string, and a
 * string `.message` off a plain thrown object. `null` means NO arm matched, and it is deliberately distinct from `''`,
 * which is what an `Error` carrying an empty message legitimately yields -- collapsing the two would make an empty
 * message indistinguishable from an unreadable throw and silently substitute a fallback for it.
 *
 * THE ARM THIS LEAF WAS MISSING, and the reason a second spelling existed. `getErrorMessage`
 * (`utils/errors/error-core.ts`) is this body plus `ERROR_MESSAGES.UNKNOWN_ERROR`, and the retry classifier in
 * `utils/async/backoff-core.ts` carried a private byte-parity copy whose own docstring recorded that it was one --
 * it could not adopt `getErrorMessage` (utils sibling) and could not adopt {@link errorMessageOrString}, which
 * renders a message-carrying object as `[object Object]`. Both now compose this, each applying its OWN miss value.
 */
export function errorMessageOrNull(error: unknown): string | null {
	if (error instanceof Error) return error.message;
	if (typeof error === 'string') return error;
	if (error !== null && typeof error === 'object' && 'message' in error) {
		const message = (error as { message?: unknown }).message;
		if (typeof message === 'string') return message;
	}
	return null;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): `"<name>: <message>"` off a real `Error`, `null` for anything else.
 * The CLASS-CARRYING sibling of {@link errorMessageOrNull}, homed beside it for the same reason it is homed here at
 * all -- a pure `utils/` leaf, and every bundled `tools/hooks/` entry, may value-import `@federa/const/*` and nothing
 * higher.
 *
 * THE NAME IS THE POINT, and it is why this is not {@link errorMessageOrString} with extra bytes. On the surfaces that
 * carried this body -- detached-child spawn failures in the PostToolUseFailure hook chain -- the message alone is
 * frequently a bare path or an empty string, and the CONSTRUCTOR name (`Error`, `TypeError`, `SystemError`) is what
 * separates an ENOENT from a stdio-shape drift in a log line nobody is watching live.
 *
 * ONE ARM, NOT THREE, and the narrowness is deliberate rather than an oversight: `.name` is meaningful only on a real
 * `Error`, so the thrown-string and message-carrying-object arms {@link errorMessageOrNull} answers have no name half
 * to report and would have to invent one. A caller holding those shapes wants that function, not this one.
 *
 * `null` FOR THE MISS, so the two live callers keep the DIFFERENT fallbacks they already had -- `String(err)` in the
 * hook dispatcher's four `console.error` legs, `''` on the issue-create record field -- through `??` rather than
 * having one imposed. That is the same reason {@link errorMessageOrNull} answers `null` instead of picking a fallback:
 * a coercer that decides the miss value for its callers forces the second spelling it was minted to prevent.
 *
 * THE `': '` JOIN IS LOAD-BEARING AND NOT COSMETIC. `QUAL-NARROWING-HELPER-CLONE-001`'s paired arm convicts the
 * hand-rolled ternary only when it spells THAT separator, because a body joining the halves any other way is not
 * interchangeable with this function and a fire there would prescribe a silent behaviour change. Change this glue and
 * the arm must move with it, or it starts prescribing a cure that reformats its callers' output.
 */
export function errorNameAndMessageOrNull(error: unknown): string | null {
	return error instanceof Error ? `${error.name}: ${error.message}` : null;
}

/** How deep the cause chain is read before the sentence stops growing: a fetch failure is two links (the TypeError and
 *  the socket error under it), a task frame over a load failure three. */
const ERROR_CAUSE_CHAIN_DEPTH = 4;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): {@link errorMessageOrString} over the error AND ITS CAUSE CHAIN,
 * the links joined on the `': '` separator, each link's `code` named in parentheses when its message does not already
 * carry it. `fetch failed: connect ECONNREFUSED 127.0.0.1:9787`; `fetch failed: other side closed (UND_ERR_SOCKET)`.
 *
 * WHY A FOURTH MEMBER (2026-09-17): Node's `fetch` throws `TypeError: fetch failed` and puts the real error -- a refused
 * connection, a reset, undici's closed socket -- on `cause`, so a sentence composed off the message alone said
 * 'fetch failed' on 1,749 refusal rows of the standard sheet with no way to tell a stack that is down from a kept-alive
 * socket a reload dropped. Bounded by depth and by identity, so a cause that points back at itself reads once.
 */
export function errorMessageWithCauses(error: unknown): string {
	const links: string[] = [];
	const seen = new Set<unknown>();
	let current: unknown = error;
	for (let depth = 0; depth < ERROR_CAUSE_CHAIN_DEPTH && current !== undefined && !seen.has(current); depth += 1) {
		seen.add(current);
		const message = errorMessageOrString(current);
		const code = current instanceof Error && 'code' in current && typeof current.code === 'string' ? current.code : null;
		links.push(code === null || message.includes(code) ? message : `${message} (${code})`);
		current = current instanceof Error ? current.cause : undefined;
	}
	return links.join(': ');
}
